import React, { useState } from 'react'
import styled from 'styled-components'
import { Button } from '~/prix/react/components/button'
import StringField from '~/prix/react/components/form/field/stringField'
import { colors } from '~/design'
import { NewFieldProps, NewQuestion, QuestionOption } from './questionnaireTypes'
import EnumeratedField from '~/prix/react/components/form/field/enumeratedField'
import { enumerated } from '~/prix'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Skeleton from '~/components/skeleton'

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  flex-direction: column;
  width: 100%;
  margin-top: 10px;
  gap: ${({ theme }) => theme.padding(1)};
  padding: 0px ${({ theme }) => theme.padding(1)};

  .enum-wrapper {
    margin: 3px 0px 6px 0px;

    span {
      display: flex;
      padding: 3px 0px 3px 0px;
    }
  }

  .boolean-wrapper {
    display: flex;
    flex: 1;
    margin: 3px 0px 3px 0px;
  }

  .question {
    display: flex;
    flex-direction: row;
    border: 1px #ccc dashed;
    margin-bottom: 10px;

    .label-column {
      display: flex;
      padding: 5px;
      width: 92%;
      flex-direction: column;
      gap: 5px;

      .title {
        padding-top: 8px;
        padding-bottom: 8px;

        .bold {
          font-weight: bold;
        }
      }

      .observation {
        padding-bottom: 8px;

        .bold {
          font-weight: bold;
        }
      }

      .options {
        .bold {
          font-weight: bold;
        }
      }
    }

    .button-column {
      display: flex;
      width: 8%;
      justify-content: center;
      align-items: center;
      flex-direction: column;

      button {
        max-width: 10px;
      }
    }
  }

  .question-options-wrapper {
    display: flex;
    flex-direction: column;

    .list {
      display: flex;
      background-color: ${colors.gray};
      padding: 6px;
      border: 2px solid white;
      border-radius: 6px;

      align-items: center;
      justify-content: center;

      .label-column {
        display: flex;
        padding: 5px;
        width: 95%;
      }

      .button-column {
        display: flex;
        padding: 5px;
        width: 5%;
        justify-content: center;
      }

      button {
        width: 25px;
        height: 25px;
      }
    }

    .button-wrapper-add-option {
      display: flex;
      padding-top: 20px;
      padding-left: 5px;
      justify-content: center;

      button {
        background-color: ${colors.prixBlue};
        border: none;
        width: 40px;
        border-radius: 6px;
      }
    }
  }

  .button {
    margin: 10px 0px 0px 0px;

    button {
      height: 30px;
    }
  }
`

const typeOptions = {
  optionButton: 'Única escolha',
  // freeText: 'free_text',
  checkBox: 'Múltipla escolha',
  // numeric: 'numeric',
  // date: 'date',
  // boolean: 'boolean',
}

const typeOptionsNormalized = {
  optionButton: 'Escolha única',
  checkBox: 'Seleção múltipla',
}

interface FieldProps {
  title: string
  observation: null
  type: string
  required: boolean
  ordinalPosition: number
  deletedAt: null
  questionOptions: never[]
  childQuestions: null
}

const FormQuestion = ({
  formFields,
  onChange,
  setFormFields,
  newField,
  setNewField,
  loading,
}: {
  formFields: any
  onChange: (field: NewFieldProps) => void
  setFormFields: (fields: NewQuestion[]) => void
  newField: NewFieldProps | null
  setNewField: (field: any) => void
  loading?: boolean
}) => {
  const [newFieldOption, setNewFieldOption] = useState('')

  const handleAddField = (field: NewFieldProps) => {
    onChange({
      ...field,
    })

    setNewField({
      title: '',
      observation: null,
      type: '',
      required: true,
      ordinalPosition: 1,
      deletedAt: null,
      questionOptions: [],
      childQuestions: null,
    })
  }

  const handleInputChange = (type: string, value: string | number | QuestionOption[]) => {
    setNewField((prevData: NewFieldProps) => ({
      ...prevData,
      [type]: value,
    }))
  }

  const handleDeleteQuestion = (title: string) => {
    const updatedFormFields = formFields.filter((field: FieldProps) => field.title !== title)
    setFormFields(updatedFormFields)
  }

  const handleAddQuestionOption = (e: { preventDefault: () => void }) => {
    e.preventDefault()
    setNewField((prevState: { questionOptions: any }) => ({
      ...prevState,
      questionOptions: [
        ...(prevState.questionOptions || []),
        { label: newFieldOption, deletedAt: null },
      ],
    }))
    setNewFieldOption('')
  }

  return (
    <Wrapper>
      {loading ? <Skeleton width='50%' height={50} /> : <h2>Insira as perguntas</h2>}
      <form onSubmit={e => e.preventDefault()}>
        {formFields?.map((field: FieldProps, index: number) => (
          <div className='question' key={index}>
            <div className='label-column'>
              <div className='title'>
                <span className='bold'>Título da pergunta: </span>
                <span>{field.title}</span>
              </div>

              {field.observation ? (
                <div className='observation'>
                  <span className='bold'>Observação: </span>
                  <span>{field.observation}</span>
                </div>
              ) : null}

              <div className='options'>
                <span className='bold'>Opções de resposta </span>
                <span>
                  (
                  {
                    typeOptionsNormalized[
                      field.type as unknown as keyof typeof typeOptionsNormalized
                    ]
                  }
                  ):
                </span>
                {field.questionOptions?.map(
                  (
                    item: {
                      label: string
                    },
                    index: number,
                  ) => (
                    <div style={{ paddingTop: '8px' }} key={index}>
                      <span> - {item.label}</span>
                    </div>
                  ),
                )}
              </div>
            </div>
            <div className='button-column'>
              <div className='button-column'>
                <Button
                  type='button'
                  onClick={e => {
                    e.preventDefault()
                    handleDeleteQuestion(field.title)
                  }}
                >
                  x
                </Button>
              </div>
            </div>
          </div>
        ))}
        {loading ? (
          <Skeleton width='auto' height={200} />
        ) : (
          <div>
            <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <StringField
                  fieldSchema={{
                    currentMeta: {
                      label: 'Pergunta',
                    },
                    currentRules: {
                      isRequired: true,
                    },
                  }}
                  value={newField?.title}
                  onDirectChange={value => handleInputChange('title', value)}
                />
              </div>

              <div>
                <StringField
                  fieldSchema={{
                    currentMeta: {
                      label: 'Observações da pergunta',
                    },

                    currentRules: {
                      isRequired: false,
                    },
                  }}
                  value={newField?.observation}
                  onDirectChange={value => handleInputChange('observation', value)}
                />
              </div>

              <div className='enum-wrapper'>
                <EnumeratedField
                  fieldSchema={enumerated(typeOptions)}
                  value={newField?.type}
                  onDirectChange={value => handleInputChange('type', value)}
                  meta={{
                    isRequired: true,
                    placeholder: 'Selecione um tipo',
                    label: 'Tipo de resposta',
                  }}
                />
              </div>

              <div className='question-options-wrapper'>
                {newField?.questionOptions?.map((item, index) => (
                  <div className='list' key={index}>
                    <div className='label-column'>{item.label}</div>
                    <div className='button-column'>
                      <Button
                        type='button'
                        onClick={e => {
                          e.preventDefault()
                          handleInputChange(
                            'questionOptions',
                            newField?.questionOptions.filter(
                              targetItem => targetItem?.label !== item?.label,
                            ),
                          )
                        }}
                      >
                        x
                      </Button>
                    </div>
                  </div>
                ))}

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                  }}
                >
                  <StringField
                    fieldSchema={{
                      currentMeta: {
                        label: 'Opções de resposta',
                      },

                      currentRules: {
                        isRequired: true,
                      },
                    }}
                    value={newFieldOption}
                    onDirectChange={value => setNewFieldOption(value)}
                    onKeyDown={(e: any) => {
                      if (
                        e.key === 'Enter' &&
                        (newField || newFieldOption || newFieldOption?.length >= 2)
                      )
                        handleAddQuestionOption(e)
                    }}
                  />
                  <div className='button-wrapper-add-option'>
                    <Button
                      title='Adicionar nova opção'
                      id='add-option-btn'
                      type='button'
                      disabled={!newField || !newFieldOption || newFieldOption?.length < 2}
                      onClick={e => handleAddQuestionOption(e)}
                    >
                      <FontAwesomeIcon icon={faPlus} width={32} height={32} fill={'#FFF'} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className='button'>
              <Button
                type='button'
                onClick={() => newField && handleAddField(newField)}
                disabled={
                  !newField ||
                  !newField.title ||
                  !newField.type ||
                  !newField?.questionOptions ||
                  newField?.questionOptions?.length < 2
                }
              >
                Adicionar pergunta
              </Button>
            </div>
          </div>
        )}
        <br />
      </form>
    </Wrapper>
  )
}

export default FormQuestion
